import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getTrainingBuilderData, saveLearningPath, saveModule, GetTrainingBuilderDataOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { BookOpen, Plus, Search, GraduationCap, Route, Archive, CheckCircle2 } from 'lucide-react';
import { isManagerPlus } from '../lib/roles';

type Module = GetTrainingBuilderDataOutputType['modules'][0];
type LPath = GetTrainingBuilderDataOutputType['learningPaths'][0];

export default function TrainingBuilder() {
  const { user } = useAuth();
  const [data, setData] = useState<GetTrainingBuilderDataOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pathDialog, setPathDialog] = useState(false);
  const [editPath, setEditPath] = useState<any>(null);

  useEffect(() => { if (user) load(); }, [user]);
  const load = () => { setLoading(true); getTrainingBuilderData({ view: isManagerPlus(user?.role) ? 'manager' : 'staff' }).then(setData).finally(() => setLoading(false)); };

  const manager = isManagerPlus(user?.role);

  const filtered = (data?.modules || []).filter(m => !search || (m.title || '').toLowerCase().includes(search.toLowerCase()));
  const published = filtered.filter(m => m.status === 'Published');
  const drafts = filtered.filter(m => m.status === 'Draft');
  const archived = filtered.filter(m => m.status === 'Archived');

  const userProgress = (data?.progress || []).filter((p: any) => {
    const tid = Array.isArray(p.trainee) ? p.trainee[0] : p.trainee;
    return tid === user?.id;
  });

  const getModuleProgress = (moduleId: string) => {
    const p = userProgress.find((pr: any) => {
      const mid = Array.isArray(pr.module) ? pr.module[0] : pr.module;
      return mid === moduleId;
    });
    return p;
  };

  const statusColor: Record<string, string> = { 'Published': 'bg-green-500/10 text-green-700', 'Draft': 'bg-yellow-500/10 text-yellow-700', 'Archived': 'bg-muted text-muted-foreground' };

  const handleSavePath = async () => {
    if (!editPath?.pathName) return;
    try {
      await saveLearningPath(editPath);
      toast.success('Learning path saved');
      setPathDialog(false);
      setEditPath(null);
      load();
    } catch { toast.error('Failed to save'); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="w-6 h-6 text-primary" /> Training & Learning Paths</h1>
          <p className="text-sm text-muted-foreground mt-1">{manager ? 'Manage training modules and learning paths' : 'Your assigned training modules'}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search modules..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-56" />
          </div>
        </div>
      </div>

      {/* My Progress Summary */}
      {!manager && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">My Progress</span>
              </div>
              <Badge variant="outline">{userProgress.filter((p: any) => p.status === 'Signed Off' || p.status === 'Quiz Passed').length} / {published.length} Completed</Badge>
              <Progress value={published.length > 0 ? (userProgress.filter((p: any) => p.status === 'Signed Off' || p.status === 'Quiz Passed').length / published.length) * 100 : 0} className="flex-1 max-w-xs" />
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="modules">
        <TabsList>
          <TabsTrigger value="modules">Modules ({published.length})</TabsTrigger>
          {manager && <TabsTrigger value="drafts">Drafts ({drafts.length})</TabsTrigger>}
          <TabsTrigger value="paths">Learning Paths ({data?.learningPaths?.length || 0})</TabsTrigger>
          {manager && <TabsTrigger value="progress">Staff Progress</TabsTrigger>}
        </TabsList>

        <TabsContent value="modules" className="space-y-3 mt-4">
          {published.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center">No published training modules</p> : published.map(m => {
            const prog = getModuleProgress(m.id);
            return (
              <Card key={m.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4 flex flex-col sm:flex-row justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{m.title}</h3>
                      <Badge className={statusColor[m.status || 'Draft']}>{m.status}</Badge>
                      {m.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{m.description || 'No description'}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      {m.estimatedDuration && <span>⏱ {m.estimatedDuration}</span>}
                      {m.department && m.department !== 'All' && <span>📂 {m.department}</span>}
                      {m.dueDate && <span>📅 Due: {new Date(m.dueDate).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {prog ? (
                      <Badge className={prog.status === 'Signed Off' || prog.status === 'Quiz Passed' ? 'bg-green-500/10 text-green-700' : 'bg-blue-500/10 text-blue-700'}>
                        <CheckCircle2 className="w-3 h-3 mr-1" />{prog.status}
                      </Badge>
                    ) : (
                      <Badge variant="outline">Not Started</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {manager && (
          <TabsContent value="drafts" className="space-y-3 mt-4">
            {drafts.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center">No draft modules</p> : drafts.map(m => (
              <Card key={m.id}>
                <CardContent className="py-4 flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold">{m.title}</h3>
                    <p className="text-sm text-muted-foreground">{m.description || 'No description'}</p>
                  </div>
                  <Badge className={statusColor.Draft}>Draft</Badge>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        )}

        <TabsContent value="paths" className="space-y-3 mt-4">
          {manager && (
            <Button onClick={() => { setEditPath({ pathName: '', description: '', targetRole: '', targetDepartment: '', moduleOrder: '', status: 'Draft' }); setPathDialog(true); }} size="sm">
              <Plus className="w-4 h-4 mr-1" /> New Path
            </Button>
          )}
          {(data?.learningPaths || []).filter(p => manager || p.status === 'Active').map((p: any) => (
            <Card key={p.id} className="cursor-pointer hover:shadow-md" onClick={() => { if (manager) { setEditPath(p); setPathDialog(true); } }}>
              <CardContent className="py-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2"><Route className="w-4 h-4 text-primary" /> {p.pathName}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{p.description || 'No description'}</p>
                    <div className="flex gap-2 mt-2">
                      {p.targetRole && <Badge variant="outline">{p.targetRole}</Badge>}
                      {p.targetDepartment && <Badge variant="outline">{p.targetDepartment}</Badge>}
                    </div>
                  </div>
                  <Badge className={statusColor[p.status || 'Draft']}>{p.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {manager && (
          <TabsContent value="progress" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Staff Training Progress</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(data?.staffList || []).map((s: any) => {
                    const staffProg = (data?.progress || []).filter((p: any) => { const tid = Array.isArray(p.trainee) ? p.trainee[0] : p.trainee; return tid === s.id; });
                    const completed = staffProg.filter((p: any) => p.status === 'Signed Off' || p.status === 'Quiz Passed').length;
                    const total = published.length;
                    return (
                      <div key={s.id} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{s.firstName} {s.lastName}</p>
                          <p className="text-xs text-muted-foreground">{s.role} · {s.department}</p>
                        </div>
                        <div className="flex items-center gap-2 w-48">
                          <Progress value={total > 0 ? (completed / total) * 100 : 0} className="flex-1" />
                          <span className="text-xs text-muted-foreground whitespace-nowrap">{completed}/{total}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Learning Path Dialog */}
      <Dialog open={pathDialog} onOpenChange={setPathDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editPath?.id ? 'Edit' : 'New'} Learning Path</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Path name" value={editPath?.pathName || ''} onChange={e => setEditPath({ ...editPath, pathName: e.target.value })} />
            <Textarea placeholder="Description" value={editPath?.description || ''} onChange={e => setEditPath({ ...editPath, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={editPath?.targetRole || ''} onValueChange={v => setEditPath({ ...editPath, targetRole: v })}>
                <SelectTrigger><SelectValue placeholder="Target Role" /></SelectTrigger>
                <SelectContent>
                  {['Trainee','Staff','Supervisor','Manager'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={editPath?.targetDepartment || ''} onValueChange={v => setEditPath({ ...editPath, targetDepartment: v })}>
                <SelectTrigger><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>
                  {['All','Front of House','Back of House','Kitchen','Management'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Module IDs (comma-separated, in order)" value={editPath?.moduleOrder || ''} onChange={e => setEditPath({ ...editPath, moduleOrder: e.target.value })} rows={2} />
            <Select value={editPath?.status || 'Draft'} onValueChange={v => setEditPath({ ...editPath, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Draft','Active','Archived'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPathDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePath}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
